import {
    AfterContentInit,
    AfterViewInit,
    Component,
    ContentChild,
    Directive,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    Output,
    ViewEncapsulation,
    computed,
    effect,
    inject, signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgControl } from '@angular/forms';
@Directive({
    selector: '[ce-input]',
})
export class ProjectedInputDirective {
    constructor(public elementRef: ElementRef) {}
}
@Component({
    selector: 'ce-form-field',
    imports: [CommonModule],
    templateUrl: './ce-form-field.component.html',
    styleUrl: './ce-form-field.component.scss',
    encapsulation: ViewEncapsulation.None,
})
export class CeFormFieldComponent implements AfterViewInit, OnDestroy, AfterContentInit {
    readonly #elementRef = inject(ElementRef);
    isInputFocused = signal<boolean>(false);
    inputValue = signal<string>('');
    isRequired = signal<boolean>(false);
    isDisabled = signal<boolean>(false);
    hasError = signal<boolean>(false);
    // default size is medium, no size class is added, however if size is set to 'sm' | 'lg' then we add a class to modify the input size
    inputSize = signal<'sm' | 'lg' | '' | 'md'>('');
    inputStatus = signal<string>('');

    #observer: MutationObserver | null = null;

    @Output() isFocused: EventEmitter<boolean> = new EventEmitter<boolean>();

    @Input()
    set size(value: 'sm' | 'lg' | 'md' | null) {
        if (value !== null) {
            this.inputSize.set(value);
        }
    }

    @Input() hideErrorSpace = false;

    @ContentChild(NgControl) ngControl!: NgControl;

    ngAfterContentInit() {
        if (this.ngControl) {
            this.ngControl.valueChanges?.subscribe((value) => {
                this.inputValue.set(value);
            });
        }
    }

    @HostListener('focusin', ['$event.target'])
    onFocusIn(target: HTMLInputElement | HTMLTextAreaElement) {
        if (this.#isCeInputElement(target)) {
            this.isInputFocused.set(true);
            this.evalutateInteractionStatus();
        }
    }

    @HostListener('focusout', ['$event.target'])
    onFocusOut(target: HTMLInputElement | HTMLTextAreaElement) {
        if (this.#isCeInputElement(target)) {
            this.isInputFocused.set(false);
            this.checkErrorOnBlur(target);
            this.evalutateInteractionStatus();
        }
    }

    @HostListener('input', ['$event.target'])
    onInput(target: HTMLInputElement | HTMLTextAreaElement) {
        if (this.#isCeInputElement(target)) {
            this.evalutateInteractionStatus();
        }
    }

    #isCeInputElement(target: HTMLElement): boolean {
        return (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') && target.getAttribute('ce-input') !== null;
    }

    inputModifiers = computed(() => {
        return this.inputStatus() + ' ' + this.inputSize();
    });

    inputSelected() {
        const inputElement = this.#elementRef.nativeElement.querySelector('input[ce-input], textarea[ce-input]');
        if (inputElement) {
            inputElement.focus();
            this.isInputFocused.set(true);
        }
    }

    evalutateInteractionStatus() {
        if (this.ngControl !== undefined) {
            const inputStateAttributes = [
                this.ngControl.dirty ? 'isDirty' : 'isPristine',
                this.ngControl.touched ? 'isTouched' : 'isUntouched',
                this.ngControl.valid ? 'isValid' : 'isUnvalid',
                this.isInputFocused() ? 'isFocused' : 'isBlurred',
            ];
            this.inputStatus.set(inputStateAttributes.join(' '));
        }
    }

    checkErrorOnBlur(target: HTMLInputElement | HTMLTextAreaElement) {
        const classesArray = Array.from(target.classList);
        this.hasError.set(classesArray.includes('ng-invalid'));
    }

    onFocusBlurToggle = effect(
        () => {
            this.isFocused.emit(this.isInputFocused());
        },
        { allowSignalWrites: true }
    );

    formFieldClassComputed = computed(() => {
        let classNames = '';
        if (this.isInputFocused()) {
            classNames += this.inputValue() !== '' ? 'isFocused' : 'isFocused--empty';
        }

        if (this.isDisabled()) {
            classNames += ' isDisabled';
        }
        if (this.hasError()) {
            classNames += ' hasError';
        }
        return classNames;
    });

    evaluateAttributes(inputAttributes: { disabled: boolean; required: boolean }) {
        this.isDisabled.set(inputAttributes?.disabled);
        this.isRequired.set(inputAttributes?.required);
    }

    ngAfterViewInit() {
        const inputElement = this.#elementRef.nativeElement.querySelector('input, textarea');
        const inputAttributes = inputElement.attributes;
        this.evaluateAttributes(inputAttributes);

        // Watch for changes in the DOM attribute when a mutation occurs.
        this.#observer = new MutationObserver((mutationsList) => {
            for (const mutation of mutationsList) {
                if (mutation.type === 'attributes') {
                    this.evaluateAttributes(inputAttributes);
                }
            }
        });

        this.#observer.observe(inputElement, { attributes: true });
    }

    ngOnDestroy() {
        if (this.#observer) {
            this.#observer.disconnect();
        }
    }
}
